import { useLayoutEffect, useMemo, useRef } from 'react';
import { getData, OAuthError } from '../backend';
import { useModal } from '../contexts';
import Modal from './Modal';
import useSWR from "swr";
import { CashIcon, OfficeBuildingIcon, TicketIcon, UserGroupIcon } from '@heroicons/react/outline';
import hsObjectTypes, { HubspotObject, HubspotObjectList, HubspotObjectListKey } from '../hsObjectTypes';
import ProLabel from './ProLabel';
import useUser from '../hooks/useUser';
import PayWallModalPro from './PayWallModalPro';
import OAuthModalHubspot from './OAuthModalHubspot';

interface Props {
  title?: string;
  subtitle?: string;
  onSelect: (selectedId: string) => void;
}

function WorkflowModalHubspotObjects(props: Props) {
  const { user } = useUser();
  const { openModal, closeModal } = useModal();

  const { data, isLoading, error } = useSWR<Array<HubspotObjectList>>(`/hubspot/object-types/`, getData, { fallbackData: hsObjectTypes, shouldRetryOnError: false });

  const scollRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    // Hack to set scrollable area back to top after it has been previously loaded
    setTimeout(() => scollRef.current?.scrollTo({ top: 0, behavior: "auto" }), 0)
  }, [scollRef]);

  const objectListMap: Record<HubspotObjectListKey, Array<HubspotObject>> = useMemo(() => {
    let map: Record<HubspotObjectListKey, Array<HubspotObject>> = {
      "PRIMARY": [],
      "SECONDARY_CONTACT": [],
      "SECONDARY_DEAL": [],
      "OBJECT_LIBRARY": [],
      "CUSTOM_OBJECTS": [],
    };

    for (const objectList of (data || []))
      map[objectList.key] = objectList.objects

    return map;
  }, [data]);

  function handleSelectObject(objectId: string, isCustomObject: boolean = false) {
    if (isCustomObject && !user?.subscription?.featureHubspotCustomObjects)
      return openModal(<PayWallModalPro />);

    props.onSelect(objectId);
  }

  return (
    <Modal
      title={props.title ?? "Select HubSpot Object"}
      subtitle={props.subtitle ?? "Select the HubSpot object you want to use in this workflow."}
      secondaryButtonProps={{ text: "Cancel", onClick: closeModal }}
    >
      <div ref={scollRef} className='flex flex-col overflow-y-scroll h-[400px] gap-2 pr-8'>
        <div className='w-full flex gap-4'>
          {objectListMap["PRIMARY"].map((object) =>
            <div key={object.id} className='flex flex-col items-center justify-center gap-2 border rounded w-full p-4 hover:border-blue cursor-pointer font-gilroy font-semibold text-gray-600 h-32 relative' onClick={() => handleSelectObject(object.id)}>
              {object.id === "0-1" && <UserGroupIcon className='w-8 h-8' />}
              {object.id === "0-2" && <OfficeBuildingIcon className='w-8 h-8' />}
              {object.id === "0-3" && <CashIcon className='w-8 h-8' />}
              {object.id === "0-5" && <TicketIcon className='w-8 h-8' />}
              {object.name}
            </div>
          )}
        </div>

        <div className='flex items-center flex-shrink-0 font-semibold text-gray-600 mt-4'>
          Custom Objects
          <ProLabel />
        </div>
        {isLoading &&
          <div className='flex-shrink-0 font-semibold text-gray-400 text-sm -mt-2'>
            Loading<span className='loading-ellipse' />
          </div>
        }
        {(error instanceof OAuthError) &&
          <div className='flex flex-col items-start'>
            <div className='flex-shrink-0 font-semibold text-gray-400 text-sm -mt-2'>
              Connect HubSpot to Portant to fetch custom objects
            </div>
            <button className='btn btn-white text-sm py-1 px-2 ' onClick={() => openModal(<OAuthModalHubspot oauthUrl={error.oauthUrl} onClose={() => openModal(<WorkflowModalHubspotObjects {...props} />)} />)}>
              Connect HubSpot
            </button>
          </div>
        }
        {objectListMap["CUSTOM_OBJECTS"].map((object) =>
          <div key={object.id} className='flex border rounded w-full p-4 hover:border-blue cursor-pointer font-gilroy font-semibold text-gray-600 flex-shrink-0' onClick={() => handleSelectObject(object.id, true)}>
            {object.name}
          </div>
        )}

        <div className='flex-shrink-0 font-semibold text-gray-600 mt-4'>
          Object Library
        </div>
        <div className='flex-shrink-0 font-semibold text-gray-400 text-sm -mt-2'>
          These object types must be enabled in your Hubspot Portal in order to be used. <a className='underline hover:text-blue' href='https://knowledge.hubspot.com/object-settings/use-the-object-library' target='_blank' rel="noreferrer">Learn more</a>
        </div>
        {objectListMap["OBJECT_LIBRARY"].map((object) =>
          <div key={object.id} className={`flex border rounded w-full p-4 font-gilroy font-semibold text-gray-600 flex-shrink-0 ${object.enabled ? "hover:border-blue cursor-pointer" : "bg-gray-100"}`} onClick={() => object.enabled ? handleSelectObject(object.id) : undefined}>
            {object.name}
            {!object.enabled &&
              <div className='bg-gray-200 font-semibold ml-auto text-gray-400 text-xs px-2 py-1 rounded'>
                Disabled
              </div>
            }
          </div>
        )}
      </div>
    </Modal>
  );
}

export default WorkflowModalHubspotObjects;
